/* BLS collection intros — show a short preview of long introductions on all screens.
 * Desktop: 3 lines. Mobile: 3 lines. Short text has no controls.
 * Preserves original HTML and links; excludes H1, product grid, SEO guide, and FAQ.
 */
(() => {
  'use strict';

  const targets = [
    '#MainContent .text-block--text_YC9YEX',
    '#MainContent .bls-murano-hero-text > p:not(.bls-murano-eyebrow)',
    '#MainContent .bls-hotel-lobby-intro__lede'
  ];
  const initialized = new WeakSet();
  let seq = 0;

  function getLineHeight(target) {
    const line = target.querySelector('p') || target;
    const styles = window.getComputedStyle(line);
    const parsed = Number.parseFloat(styles.lineHeight);
    const font = Number.parseFloat(styles.fontSize) || 16;
    return Number.isFinite(parsed) ? parsed : font * 1.65;
  }

  function initElement(target) {
    if (initialized.has(target) || !target.textContent?.trim()) return;
    initialized.add(target);
    target.classList.add('bls-ci__body');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bls-ci__toggle';
    button.hidden = true;
    if (!target.id) target.id = 'bls-collection-intro-' + (++seq);
    button.setAttribute('aria-controls', target.id);
    button.setAttribute('aria-expanded', 'false');
    target.insertAdjacentElement('afterend', button);

    const originalTabindexes = new Map();
    const focusables = () => Array.from(target.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'));
    let expanded = false;

    function restoreFocusableElements() {
      for (const [node, previous] of originalTabindexes) {
        if (!node.isConnected) continue;
        if (previous === null) node.removeAttribute('tabindex');
        else node.setAttribute('tabindex', previous);
      }
      originalTabindexes.clear();
    }

    function disableClippedFocus() {
      restoreFocusableElements();
      const clipBottom = target.getBoundingClientRect().bottom - 1;
      for (const link of focusables()) {
        if (link.getBoundingClientRect().bottom > clipBottom) {
          originalTabindexes.set(link, link.getAttribute('tabindex'));
          link.setAttribute('tabindex', '-1');
        }
      }
    }

    function measureAndRender() {
      if (!target.isConnected) return;
      target.classList.remove('bls-ci__body--collapsed');
      restoreFocusableElements();

      const lineHeight = getLineHeight(target);
      const visibleHeight = Math.ceil(3.05 * lineHeight);
      target.style.setProperty('--bls-ci-max-height', visibleHeight + 'px');

      const naturalHeight = target.scrollHeight;
      const longEnough = naturalHeight > visibleHeight + Math.max(6, lineHeight * .35);
      button.hidden = !longEnough;

      if (!longEnough) expanded = false;
      const collapse = longEnough && !expanded;
      target.classList.toggle('bls-ci__body--collapsed', collapse);

      const spanish = document.documentElement.lang.toLowerCase().startsWith('es');
      button.textContent = expanded
        ? (spanish ? 'Mostrar menos' : 'Show less')
        : (spanish ? 'Leer más' : 'Read more');
      button.setAttribute('aria-expanded', expanded && longEnough ? 'true' : 'false');

      if (collapse) disableClippedFocus();
    }

    button.addEventListener('click', () => {
      expanded = !expanded;
      measureAndRender();
    });

    let scheduled = false;
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        measureAndRender();
      });
    }
    window.addEventListener('resize', schedule, { passive: true });
    document.fonts?.ready?.then(schedule);
    requestAnimationFrame(measureAndRender);
  }

  function initialize(root = document) {
    for (const selector of targets) {
      root.querySelectorAll(selector).forEach(initElement);
      if (root.matches?.(selector)) initElement(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
